/**
 * Los hooks de datos de proveedores y su cuenta corriente.
 *
 * Viven acá y no en `lib/queries.ts` por la misma razón que los de compras: son
 * de este módulo. `useProveedores` sigue en `lib/queries.ts` porque lo usan los
 * desplegables de media aplicación.
 *
 * QUÉ SE INVALIDA AL PAGAR. Un pago mueve tres cosas a la vez: el saldo del
 * proveedor, su extracto, y la caja de donde salió la plata. Olvidarse de la
 * caja deja el saldo de la caja viejo en pantalla hasta que alguien recargue.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, ErrorApi } from "@/lib/api";
import { claves } from "@/lib/queries";
import type { Moneda } from "@tora/shared";
import type { Pagina } from "@/lib/tipos";

import type { DeudaProveedor, MovimientoProveedor, SaldoProveedor } from "./logica";

export const clavesProveedores = {
  conSaldo: ["proveedores", "con-saldo"] as const,
  ficha: (id: number) => ["proveedores", "ficha", id] as const,
  extracto: (id: number) => ["proveedores", "extracto", id] as const,
  // `todas` entra en la clave: son dos conjuntos distintos, y cachearlos juntos
  // devolveria solo las abiertas donde se pidieron todas.
  deudas: (id: number, todas: boolean) => ["proveedores", "deudas", id, todas] as const,
};

export interface FichaProveedorDatos {
  proveedor: SaldoProveedor;
  saldo: SaldoProveedor | null;
}

/** Los que deben algo, ordenados por quién espera hace más. */
export function useProveedoresConSaldo(): UseQueryResult<Pagina<SaldoProveedor>, ErrorApi> {
  return useQuery<Pagina<SaldoProveedor>, ErrorApi>({
    queryKey: clavesProveedores.conSaldo,
    queryFn: ({ signal }) =>
      api.get<Pagina<SaldoProveedor>>("/proveedores", { params: { con_saldo: "1" }, signal }),
    staleTime: 60_000,
  });
}

export function useFichaProveedor(
  id: number | null,
): UseQueryResult<FichaProveedorDatos, ErrorApi> {
  return useQuery<FichaProveedorDatos, ErrorApi>({
    queryKey: clavesProveedores.ficha(id ?? 0),
    queryFn: ({ signal }) => api.get<FichaProveedorDatos>(`/proveedores/${id}`, { signal }),
    enabled: id !== null,
  });
}

export function useExtractoProveedor(
  id: number | null,
): UseQueryResult<Pagina<MovimientoProveedor>, ErrorApi> {
  return useQuery<Pagina<MovimientoProveedor>, ErrorApi>({
    queryKey: clavesProveedores.extracto(id ?? 0),
    queryFn: ({ signal }) =>
      api.get<Pagina<MovimientoProveedor>>(`/proveedores/${id}/extracto`, { signal }),
    enabled: id !== null,
  });
}

/**
 * Lo que se le debe a un proveedor, factura por factura.
 *
 * Con `todas`, tambien las ya canceladas: es lo que necesita el extracto para
 * poder decir "pagada" en el renglon de una compra. El dialogo de pago pide
 * solo las abiertas, que son las que el FIFO va a cancelar.
 */
export function useDeudasProveedor(
  id: number | null,
  todas = false,
): UseQueryResult<Pagina<DeudaProveedor>, ErrorApi> {
  return useQuery<Pagina<DeudaProveedor>, ErrorApi>({
    queryKey: clavesProveedores.deudas(id ?? 0, todas),
    queryFn: ({ signal }) =>
      api.get<Pagina<DeudaProveedor>>(`/proveedores/${id}/deudas`, {
        params: todas ? { todas: "1" } : {},
        signal,
      }),
    enabled: id !== null,
  });
}

export interface PagoProveedor {
  cuenta_id: number;
  medio: string;
  importe: number;
  /** Contra qué moneda de deuda se imputa. Sin esto, la de la cuenta. */
  moneda_deuda?: Moneda;
  /** La que pasa el proveedor. Es la que hace medible el arbitraje. */
  cotizacion?: number;
}

export interface DatosPago {
  pagos: PagoProveedor[];
  detalle?: string;
  observaciones?: string;
}

export function usePagarProveedor(id: number): UseMutationResult<unknown, ErrorApi, DatosPago> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, DatosPago>({
    mutationFn: (datos) => api.post(`/proveedores/${id}/pago`, datos),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.proveedores });
      void qc.invalidateQueries({ queryKey: claves.cajas });
    },
  });
}

export interface DatosAjuste {
  tipo: "AJUSTE+" | "AJUSTE-" | "SALDO_INICIAL" | "NC";
  importe: number;
  moneda: Moneda;
  cotizacion?: number;
  detalle?: string;
}

export function useAjustarProveedor(id: number): UseMutationResult<unknown, ErrorApi, DatosAjuste> {
  const qc = useQueryClient();
  return useMutation<unknown, ErrorApi, DatosAjuste>({
    mutationFn: (datos) => api.post(`/proveedores/${id}/ajuste`, datos),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: claves.proveedores });
    },
  });
}
